import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';

import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { Meta } from '../seo/Meta.jsx';
import { useSession } from '../lib/auth.js';

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const RESERVED = new Set([
  'about','admin','api','app','auth','billing','blog','console','contact','dashboard',
  'docs','help','legal','login','logout','mail','private','privacy','public','root',
  'security','settings','signup','status','support','system','terms','www',
]);

function deriveSlug(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9 -]/g, '')
    .trim()
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);
}

function slugError(slug) {
  if (!slug) return 'Slug is required.';
  if (slug.length < 3 || slug.length > 30) return 'Slug must be 3–30 characters.';
  if (!/^[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?$/.test(slug)) {
    return 'Slug must be lowercase letters, digits, or hyphens; cannot start or end with hyphen.';
  }
  if (RESERVED.has(slug)) return 'This slug is reserved; pick a different one.';
  return null;
}

export default function WorkspaceSetup() {
  const navigate = useNavigate();
  const { workspaces, loading } = useSession();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // If the user already has a workspace, skip setup and go to dashboard.
  useEffect(() => {
    if (!loading && workspaces && workspaces.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [loading, workspaces, navigate]);

  // Auto-derive slug from name unless user has typed in the slug field.
  useEffect(() => {
    if (!slugTouched) setSlug(deriveSlug(name));
  }, [name, slugTouched]);

  const slugProblem = useMemo(() => slugError(slug), [slug]);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr('Workspace name is required.');
      return;
    }
    if (slugProblem) {
      setErr(slugProblem);
      return;
    }
    setBusy(true);
    try {
      const url = `${API_BASE}/v1/workspace`;
      console.log('[workspace-setup] POST', url, { name: name.trim(), slug });
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name: name.trim(), slug }),
      });
      console.log('[workspace-setup] response', res.status, res.headers.get('content-type'));
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = data?.error?.message || data?.error?.code || `HTTP ${res.status}`;
        console.warn('[workspace-setup] error', res.status, data);
        setErr(`${msg} (status ${res.status})`);
        return;
      }
      console.log('[workspace-setup] created', data);
      // Hard nav so AuthGuard re-fetches session fresh on /dashboard.
      window.location.replace('/dashboard');
    } catch (ex) {
      console.error('[workspace-setup] exception', ex);
      setErr(`${ex.message || 'Network error'} (check browser console + Sentry)`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Meta title="Set up your workspace" path="/workspace-setup" />
      <Header />
      <main className="container" style={{ paddingTop: 'var(--space-10)', paddingBottom: 'var(--space-12)', maxWidth: 520 }}>
        <h1 style={{ fontSize: 'var(--fs-32)', fontWeight: 700, margin: 0 }}>
          Name your workspace
        </h1>
        <p style={{ fontSize: 'var(--fs-15)', color: 'var(--fg-2)', marginTop: 8 }}>
          Pick a name and URL slug. The slug shows up in dashboard URLs and webhook payloads. You can change the name later  -  the slug is permanent.
        </p>

        <form onSubmit={submit} style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="Workspace name" required>
            <input
              type="text"
              required
              minLength={2}
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Procurement"
              style={inputStyle}
            />
          </Field>
          <Field label="Slug" required hint="Lowercase letters, digits, hyphens. 3–30 chars.">
            <input
              type="text"
              required
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              placeholder="acme"
              pattern="[a-z0-9](?:[a-z0-9-]{1,28}[a-z0-9])?"
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}
            />
            {slug && slugProblem && (
              <span role="alert" style={{ fontSize: 'var(--fs-12)', color: 'var(--danger)' }}>{slugProblem}</span>
            )}
          </Field>

          {err && (
            <div role="alert" style={{ padding: 12, background: '#fef2f2', border: '1px solid var(--danger)', borderRadius: 'var(--r-2)', color: 'var(--danger)', fontSize: 'var(--fs-14)' }}>
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !!slugProblem || !name.trim()}
            style={{
              marginTop: 8,
              padding: '12px 20px',
              background: 'var(--accent)',
              color: 'var(--accent-fg)',
              border: 0,
              borderRadius: 'var(--r-3)',
              fontWeight: 600,
              fontSize: 'var(--fs-15)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy || slugProblem ? 0.6 : 1,
            }}
          >
            {busy ? <Loader2 size={16} className="spin" /> : <ArrowRight size={16} strokeWidth={2.5} />}
            Create workspace
          </button>
        </form>
      </main>
      <Footer />
    </>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 'var(--fs-13)', fontWeight: 600, color: 'var(--fg-1)' }}>
        {label}
        {required && <span style={{ color: 'var(--accent)', marginLeft: 4 }}>*</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 'var(--fs-12)', color: 'var(--fg-3)' }}>{hint}</span>}
    </label>
  );
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--line-2)',
  borderRadius: 'var(--r-2)',
  fontSize: 'var(--fs-14)',
  fontFamily: 'inherit',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
};
